// L'éditeur de programme du dashboard coach web (modale « Nouveau programme »),
// reproduit à ses dimensions réelles puis réduit dans la fenêtre. Largeur :
// 720 px, celle de la modale sur un écran de 750 px (96vw) ; la grille y tient
// sans rien couper. Disposition, textes et styles repris de components/coach/
// CoachPrograms.js (ProgramEditorModal) et des règles .mens-modal, .prg-cell
// et .bulk-assign-step de App.css (thème clair du dashboard).
//
// Animé une fois à l'apparition : la grille se remplit modèle par modèle
// (Push lundi, Pull mercredi, Jambes vendredi) et le compteur suit.
import React, { useEffect, useState } from "react";
import Apercu from "./Apercu";
import { ModaleWeb } from "./Cadres";
import useApparition from "../useApparition";
import { GRILLE_PROGRAMME } from "./donneesDemo";

const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const SEMAINES = 4;
const MODELES_DISPO = 9;
// Largeur de la modale (720 px), plus le voile de 24 px de chaque côté.
const LARGEUR = 768;
const DEBUT = 500;
const INTERVALLE = 220;

const mouvementReduit = () =>
	typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Cellules remplies, dans l'ordre où le coach les pose.
const CELLULES = [];
for (let s = 1; s <= SEMAINES; s++) {
	GRILLE_PROGRAMME.forEach((c) => CELLULES.push({ ...c, semaine: s, cle: `${s}-${c.jour}` }));
}
const RANG = new Map(CELLULES.map((c, i) => [c.cle, i]));

const Plus = () => (
	<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
		<line x1="12" y1="5" x2="12" y2="19" />
		<line x1="5" y1="12" x2="19" y2="12" />
	</svg>
);

const Rouage = () => (
	<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
		<circle cx="12" cy="12" r="3" />
		<path d="M19.4 15a1.6 1.6 0 0 0 .3 1.7l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.7-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.09A1.6 1.6 0 0 0 9 19.4a1.6 1.6 0 0 0-1.7.3l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.6 1.6 0 0 0 4.6 15a1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.09A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.3-1.7l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.6 1.6 0 0 0 9 4.6a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.09a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.7-.3l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.3 1.7 1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.09a1.6 1.6 0 0 0-1.5 1z" />
	</svg>
);

function Cellule({ cellule }) {
	if (!cellule) {
		return (
			<div className="gs-prg-cell">
				<span className="gs-prg-ajout">
					<Plus />
				</span>
			</div>
		);
	}
	// Semaine 1 : la progression est déjà paramétrée (roue crantée).
	const programme = cellule.semaine === 1;
	return (
		<div className={`gs-prg-cell gs-prg-cell--pleine${programme ? " gs-prg-cell--programme" : ""}`}>
			<span className="gs-prg-nom">{cellule.nom}</span>
			<span className="gs-prg-pied">
				<span className={`gs-prg-statut${programme ? " on" : ""}`}>{programme ? "● programmé" : "à définir"}</span>
				<span className="gs-prg-actions">
					<span className="gs-prg-rouage">
						<Rouage />
					</span>
					<span className="gs-prg-retirer">×</span>
				</span>
			</span>
		</div>
	);
}

export default function ApercuProgramme({ className = "" }) {
	const [refGrille, visible] = useApparition(0.4);
	const [reduit] = useState(mouvementReduit);
	const [posees, setPosees] = useState(() => (mouvementReduit() ? CELLULES.length : 0));

	useEffect(() => {
		if (!visible || reduit) return undefined;
		const minuteries = CELLULES.map((_, i) => setTimeout(() => setPosees(i + 1), DEBUT + i * INTERVALLE));
		return () => minuteries.forEach(clearTimeout);
	}, [visible, reduit]);

	const pluriel = posees > 1 ? "s" : "";

	return (
		<Apercu
			className={className}
			description="Éditeur de programme du dashboard coach : « PPL 4 semaines », une grille de quatre semaines où Push, Pull et Jambes se posent le lundi, le mercredi et le vendredi, soit 12 séances programmées."
		>
			{/* .mens-modal-overlay : voile noir à 60 %, flou de 4 px. */}
			<ModaleWeb largeur={LARGEUR} marge={24} voile="rgba(0, 0, 0, 0.6)" flou={4} page="programmes">
				<div className="gs-prg-modale">
					<div className="gs-prg-tete">
						<h3>Nouveau programme</h3>
						<span className="gs-prg-fermer">×</span>
					</div>

					<div className="gs-prg-haut">
						<div className="gs-prg-ligne">
							<div>
								<span className="gs-prg-label">Nom</span>
								<span className="gs-prg-champ">PPL 4 semaines</span>
							</div>
							<div>
								<span className="gs-prg-label">Semaines</span>
								<span className="gs-prg-champ">{SEMAINES}</span>
							</div>
						</div>
						<div className="gs-prg-description">
							<span className="gs-prg-label">Description (optionnelle)</span>
							<span className="gs-prg-champ gs-prg-champ--zone">Push, Pull, Jambes · 3 séances par semaine</span>
						</div>
						<div className="gs-prg-etape">
							<span className="gs-prg-etape-num">1</span>
							<span className="gs-prg-etape-label">Grille : sélectionne un modèle par jour</span>
						</div>
					</div>

					<div className="gs-prg-zone" ref={refGrille}>
						<table className="gs-prg-table">
							<thead>
								<tr>
									<th className="gs-prg-th-semaine">Semaine</th>
									{JOURS.map((j) => (
										<th key={j}>{j}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{Array.from({ length: SEMAINES }, (_, i) => i + 1).map((semaine) => (
									<tr key={semaine}>
										<td className="gs-prg-td-semaine">S{semaine}</td>
										{JOURS.map((_, i) => {
											const cle = `${semaine}-${i + 1}`;
											const rang = RANG.get(cle);
											const cellule = rang !== undefined && rang < posees ? CELLULES[rang] : null;
											return (
												<td key={cle}>
													<Cellule cellule={cellule} />
												</td>
											);
										})}
									</tr>
								))}
							</tbody>
						</table>
					</div>

					<div className="gs-prg-compteur">
						{posees} séance{pluriel} programmée{pluriel} · {MODELES_DISPO} modèles dispo
					</div>

					<div className="gs-prg-boutons">
						<span className="gs-prg-btn">Annuler</span>
						<span className="gs-prg-btn gs-prg-btn--plein">Enregistrer</span>
					</div>
				</div>
			</ModaleWeb>
		</Apercu>
	);
}
